import { Suspense, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Dialog,
  DialogFooter,
} from '@/components/ui/Dialog';
import { ResponsiveDialogContent } from '@/components/ui/ResponsiveDialog';
import { ResponsiveDialogHeader } from '@/components/ui/ResponsiveDialogHeader';
import { DeleteConfirmation } from '@/components/ui/DeleteConfirmation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Plus, Briefcase, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Badge } from '@/components/ui/Badge';
import { formatCurrency } from '@/utils/formatters';
import { FadeIn } from '@/components/animations/FadeIn';
import { EmptyState } from '@/components/emptyState/EmptyState';
import { TableSkeleton } from '@/components/loading/LoadingState';
import { graphql } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime/lib/store/RelayStoreTypes';
import projectsQuery from '@/__generated__/ProjectsPageQuery.graphql.ts';
import createProjectMutation from '@/__generated__/ProjectsPageMutation.graphql.ts';
import updateProjectMutation from '@/__generated__/ProjectsPageUpdateProjectMutation.graphql.ts';
import deleteProjectMutation from '@/__generated__/ProjectsPageDeleteProjectMutation.graphql.ts';
import type { ProjectsPageQuery as ProjectsPageQueryType } from '@/__generated__/ProjectsPageQuery.graphql.ts';

type EditableProject = {
  id: string;
  name: string;
  hourlyRate: number | null;
  currency: 'BRL' | 'USD';
  status: 'in_progress' | 'completed' | 'archived';
  color: string | null;
};

const projectsQueryGraphql = graphql`
  query ProjectsPageQuery {
    projects {
      id
      name
      hourlyRate
      currency
      status
      color
    }
  }
`;

const createProjectGraphql = graphql`
  mutation ProjectsPageMutation(
    $name: String!
    $hourlyRate: Float
    $currency: Currency
    $status: ProjectStatus
    $color: String
  ) {
    createProject(
      name: $name
      hourlyRate: $hourlyRate
      currency: $currency
      status: $status
      color: $color
    ) {
      id
      name
      hourlyRate
      currency
      status
      color
    }
  }
`;

const updateProjectGraphql = graphql`
  mutation ProjectsPageUpdateProjectMutation(
    $id: ID!
    $name: String
    $hourlyRate: Float
    $currency: Currency
    $status: ProjectStatus
    $color: String
  ) {
    updateProject(
      id: $id
      name: $name
      hourlyRate: $hourlyRate
      currency: $currency
      status: $status
      color: $color
    ) {
      id
      name
      hourlyRate
      currency
      status
      color
    }
  }
`;

const deleteProjectGraphql = graphql`
  mutation ProjectsPageDeleteProjectMutation($id: ID!) {
    deleteProject(id: $id)
  }
`;

const formSchema = z.object({
  name: z.string().min(1),
  hourlyRate: z.coerce.number().min(0).optional(),
  currency: z.enum(['BRL', 'USD']),
  status: z.enum(['in_progress', 'completed', 'archived']),
  color: z
    .string()
    .regex(/^#([0-9A-Fa-f]{6})$/, 'Use a hex color like #6366f1')
    .optional(),
});

// Implements github.com/HallanCosta/daysheetly-issues#1: Edit Project
// from the projects table. Same form serves Add and Edit modes; the
// editingProject state controls which mutation fires on submit.
type FormValues = z.infer<typeof formSchema>;

const statusLabels: Record<string, string> = {
  in_progress: 'In Progress',
  completed: 'Completed',
  archived: 'Archived',
};

const statusColors: Record<
  string,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive'
> = {
  in_progress: 'secondary',
  completed: 'success',
  archived: 'default',
};

const projectColors = ['#2563EB', '#14B8A6', '#8B5CF6', '#F97316', '#EC4899', '#06B6D4', '#6B7280'];

export function ProjectsPage() {
  const [isOpen, setIsOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<EditableProject | null>(null);
  const [deletingProject, setDeletingProject] = useState<EditableProject | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [commitCreate, isCreating] = useMutation(createProjectMutation);
  const [commitUpdate, isUpdating] = useMutation(updateProjectMutation);
  const [commitDelete, isDeleting] = useMutation(deleteProjectMutation);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { currency: 'BRL', status: 'in_progress', color: '#2563EB' },
  });

  const openAddDialog = () => {
    setEditingProject(null);
    reset({ name: '', hourlyRate: undefined, currency: 'BRL', status: 'in_progress', color: '#2563EB' });
    setIsOpen(true);
  };

  const openEditDialog = (project: EditableProject) => {
    setEditingProject(project);
    reset({
      name: project.name,
      hourlyRate: project.hourlyRate ?? undefined,
      currency: project.currency,
      status: project.status,
      color: project.color ?? '#2563EB',
    });
    setIsOpen(true);
  };

  const onSubmit = (values: FormValues) => {
    if (editingProject) {
      // Update existing project
      commitUpdate({
        variables: {
          id: editingProject.id,
          name: values.name,
          hourlyRate: values.hourlyRate ?? null,
          currency: values.currency,
          status: values.status,
          color: values.color ?? null,
        },
        updater: (store: RecordSourceSelectorProxy) => {
          const root = store.getRootField('updateProject');
          if (!root) return;
          const id = root.getValue('id') as string;
          const record = store.get(id);
          if (record) {
            record.setValue(root.getValue('name'), 'name');
            record.setValue(root.getValue('hourlyRate'), 'hourlyRate');
            record.setValue(root.getValue('currency'), 'currency');
            record.setValue(root.getValue('status'), 'status');
            record.setValue(root.getValue('color'), 'color');
          }
        },
        onCompleted: () => {
          setIsOpen(false);
          setEditingProject(null);
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to update project');
        },
      });
    } else {
      // Create new project
      commitCreate({
        variables: {
          name: values.name,
          hourlyRate: values.hourlyRate || null,
          currency: values.currency,
          status: values.status,
          color: values.color || null,
        },
        updater: (store: RecordSourceSelectorProxy) => {
          const root = store.getRootField('createProject');
          if (!root) return;
          const rootRecord = store.getRoot();
          const projects = rootRecord?.getLinkedRecords('projects');
          if (rootRecord && projects) {
            rootRecord.setLinkedRecords([...projects, root], 'projects');
          }
        },
        onCompleted: () => {
          reset();
          setIsOpen(false);
        },
      });
    }
  };

  const openDeleteDialog = (project: EditableProject) => {
    setDeletingProject(project);
    setDeleteConfirmText('');
  };

  const closeDeleteDialog = () => {
    setDeletingProject(null);
    setDeleteConfirmText('');
  };

  const confirmDelete = () => {
    if (!deletingProject) return;
    commitDelete({
      variables: { id: deletingProject.id },
      updater: (store: RecordSourceSelectorProxy) => {
        const root = store.getRoot();
        const projects = root?.getLinkedRecords('projects');
        if (root && projects) {
          root.setLinkedRecords(
            projects.filter((p) => p.getDataID() !== deletingProject.id),
            'projects',
          );
        }
      },
      onCompleted: (deleted) => {
        if (deleted) {
          toast.success(`Projeto "${deletingProject.name}" deletado`);
        }
        closeDeleteDialog();
      },
      onError: () => {
        toast.error('Falha ao deletar projeto');
      },
    });
  };

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <FadeIn>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
            <p className="text-sm text-muted-foreground">Manage projects and billing rates.</p>
          </div>
          <Button onClick={openAddDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Add Project
          </Button>
        </div>
      </FadeIn>

      <FadeIn delay={0.1}>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <Suspense fallback={<TableSkeleton rows={5} columns={4} />}>
              <ProjectsTable
                onAdd={openAddDialog}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
              />
            </Suspense>
          </CardContent>
        </Card>
      </FadeIn>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) setEditingProject(null);
          setIsOpen(open);
        }}
      >
        <ResponsiveDialogContent
          data-testid="project-form-dialog"
          className="sm:!max-w-[560px]"
        >
          <ResponsiveDialogHeader
            title={editingProject ? 'Edit Project' : 'New Project'}
            onBack={() => setIsOpen(false)}
          />
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-5 sm:overflow-visible sm:px-0 sm:py-0"
          >
            <div className="space-y-2">
              <Label htmlFor="name">Project name</Label>
              <Input id="name" className="h-11" placeholder="e.g. Website redesign" {...register('name')} />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-4">
              <div className="space-y-2">
                <Label htmlFor="rate">Hourly rate</Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  className="h-11"
                  // valueAsNumber would turn an EMPTY input into NaN, which
                  // fails z.number() and silently blocks the submit (no error
                  // is rendered for this field). Map '' to undefined instead.
                  {...register('hourlyRate', {
                    setValueAs: (value) =>
                      value === '' || value == null ? undefined : Number(value),
                  })}
                />
                {errors.hourlyRate && (
                  <p className="text-sm text-destructive">{errors.hourlyRate.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BRL">BRL</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color-picker">Project color</Label>
              <Controller
                name="color"
                control={control}
                render={({ field }) => {
                  const value = field.value?.toUpperCase() ?? '#2563EB';
                  return (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
                        {projectColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            aria-label={`Use color ${color}`}
                            data-testid={`project-color-${color.slice(1)}`}
                            onClick={() => field.onChange(color)}
                            className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                              value === color
                                ? 'border-foreground ring-2 ring-primary ring-offset-2'
                                : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                      <input
                        id="color-picker"
                        type="color"
                        value={value}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        className="h-10 w-10 cursor-pointer rounded-md border border-input bg-transparent"
                      />
                      <Input
                        id="colorHex"
                        value={value}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        placeholder="#6366f1"
                        className="h-11 max-w-[180px]"
                      />
                      </div>
                    </div>
                  );
                }}
              />
              {errors.color && <p className="text-sm text-destructive">{errors.color.message}</p>}
            </div>

            <DialogFooter className="sticky bottom-0 z-10 !flex-row gap-3 border-t bg-background py-4 [&>button]:min-w-0 [&>button]:flex-1 sm:static sm:space-x-0 sm:border-0 sm:bg-transparent sm:py-0">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={isCreating || isUpdating}>
                {editingProject ? 'Save Changes' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </ResponsiveDialogContent>
      </Dialog>

      <DeleteConfirmation
        open={!!deletingProject}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
        title="Deletar projeto?"
        description="Esta ação não pode ser desfeita. Todas as tasks vinculadas também serão removidas."
        confirmLabel="Deletar"
        confirmingLabel="Deletando..."
        cancelLabel="Cancelar"
        onConfirm={confirmDelete}
        confirmDisabled={deleteConfirmText !== deletingProject?.name}
        isConfirming={isDeleting}
        testId="project-delete-confirmation"
        actionsTestId="project-delete-confirmation-actions"
        confirmationInput={deletingProject ? {
          label: <>Digite <span className="font-semibold">"{deletingProject.name}"</span> para confirmar</>,
          value: deleteConfirmText,
          onChange: setDeleteConfirmText,
          id: 'delete-confirm',
          testId: 'delete-confirm-input',
        } : undefined}
      />
    </div>
  );
}

/**
 * Inner component that owns the project list data fetch.
 * useLazyLoadQuery suspends, so we wrap this in <Suspense> at the parent and
 * show a table skeleton while it loads.
 */
function ProjectsTable({
  onAdd,
  onEdit,
  onDelete,
}: {
  onAdd: () => void;
  onEdit: (project: EditableProject) => void;
  onDelete: (project: EditableProject) => void;
}) {
  const data = useLazyLoadQuery<ProjectsPageQueryType>(projectsQuery, {});

  if (data.projects.length === 0) {
    return (
      <EmptyState
        title="No projects yet"
        description="Create a project to start tracking time."
        icon={<Briefcase className="h-6 w-6" />}
        action={{ label: 'Add Project', onClick: onAdd }}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Hourly Rate</TableHead>
            <TableHead className="hidden sm:table-cell">Currency</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-12 text-right">Ação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.projects.map((project) => (
            <TableRow key={project.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {project.color && (
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                  )}
                  {project.name}
                </div>
              </TableCell>
              <TableCell>
                {project.hourlyRate != null
                  ? formatCurrency(project.hourlyRate, project.currency)
                  : '-'}
              </TableCell>
              <TableCell className="hidden sm:table-cell">{project.currency}</TableCell>
              <TableCell>
                <Badge variant={statusColors[project.status] ?? 'default'}>
                  {statusLabels[project.status] ?? project.status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Ações para ${project.name}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() =>
                        onEdit({
                          id: project.id,
                          name: project.name,
                          hourlyRate: project.hourlyRate,
                          // Relay types enums with a "%future added value"
                          // arm; the server only ever returns these two.
                          currency: project.currency as EditableProject['currency'],
                          status: project.status as EditableProject['status'],
                          color: project.color ?? null,
                        })
                      }
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar projeto
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() =>
                        onDelete({
                          id: project.id,
                          name: project.name,
                          hourlyRate: project.hourlyRate,
                          currency: project.currency as EditableProject['currency'],
                          status: project.status as EditableProject['status'],
                          color: project.color ?? null,
                        })
                      }
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Deletar projeto
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
